import React from 'react';
import { Link } from 'react-router-dom';
import { BadgePercent, CalendarClock, Package } from 'lucide-react';
import { t } from '@/i18n';

const PACKS = [
  { n: 25, each: 115 },
  { n: 50, each: 115 },
  { n: 99, each: 115 },
];
const usd = (v) => `$${v.toLocaleString('en-US')}`;

/** Advertises prepaid NOC packages and the $25 no-notary discount. */
export const NocPackagesPromo = ({ dark = false }) => (
  <section className={`rounded-2xl border p-6 sm:p-8 space-y-6 ${dark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200 text-[#0F172A]'}`}
           data-testid="noc-packages-promo">
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="text-[#F97316] font-semibold uppercase tracking-wide text-xs flex items-center gap-1"><Package className="h-4 w-4" />{t('Prepaid NOC packages')}</p>
        <h2 className="text-2xl font-bold mt-1">{t('Prepay fully researched NOCs at the volume rate')}</h2>
        <p className={`mt-2 max-w-2xl text-sm ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
          {t('Buy 25 or more up front and every one is $115 instead of $135 — research, preparation, notarization and e-recording included. Use them on any Florida job, one at a time or in a bulk upload.')}
        </p>
      </div>
      <p className={`text-sm flex items-center gap-1 ${dark ? 'text-emerald-300' : 'text-emerald-700'} font-semibold`} data-testid="noc-packages-expiry">
        <CalendarClock className="h-4 w-4" />{t('Prepaid NOCs stay good for 90 days')}
      </p>
    </div>
    <div className="grid sm:grid-cols-3 gap-3">
      {PACKS.map((p) => (
        <div key={p.n} className={`rounded-xl border p-4 transition-transform hover:-translate-y-0.5 ${dark ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-slate-50'}`} data-testid={`noc-package-${p.n}`}>
          <p className="text-sm font-semibold">{p.n} {t('NOCs')}</p>
          <p className="text-3xl font-bold mt-1">{usd(p.n * p.each)}</p>
          <p className={`text-xs mt-1 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{usd(p.each)} {t('each')} · {t('you save')} {usd(p.n * (135 - p.each))}</p>
        </div>
      ))}
    </div>
    <p className={`text-xs ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{t('Smaller packages (5–24) are sold at your current rate. Unused NOCs expire 90 days after purchase. 50+ NOCs a month? Talk to sales for a negotiated rate.')}</p>
    <div className="grid sm:grid-cols-2 gap-3">
      <div className={`rounded-xl border p-4 ${dark ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-white'}`} data-testid="noc-self-service-promo">
        <p className="text-sm font-semibold">{t('Self-service NOC')} · <span className="text-[#F97316]">$50</span> {t('+ county recording fees')}</p>
        <p className={`text-xs mt-1 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{t('Already have the owner, legal description and folio? Fill in the form, the owner books their online notary time, and it is e-recorded and sent back to you automatically. No research.')}</p>
      </div>
      <div className={`rounded-xl border p-4 ${dark ? 'border-white/15 bg-white/5' : 'border-slate-200 bg-white'}`} data-testid="noc-byod-promo">
        <p className="text-sm font-semibold">{t('Bring your own document — e-recording only')} · <span className="text-[#F97316]">$35</span> {t('+ $10 a page')}</p>
        <p className={`text-xs mt-1 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{t('Already signed and notarized? Upload the PDF and we e-record it with the county — NOCs, payment bonds, terminations and more. $25 for enterprise and partner accounts.')}</p>
      </div>
    </div>
    <div className={`flex flex-wrap items-center gap-3 rounded-xl p-4 ${dark ? 'bg-emerald-500/15 border border-emerald-400/30' : 'bg-emerald-50 border border-emerald-200'}`} data-testid="noc-no-notary-promo">
      <BadgePercent className={`h-5 w-5 ${dark ? 'text-emerald-300' : 'text-emerald-700'}`} />
      <p className="text-sm flex-1 min-w-[240px]">
        <b>{t("Don't need remote online notarization? Take $25 off.")}</b>{' '}
        {t('Opt out when you order, sign before your own notary, upload the notarized PDF and we still e-record it with the county.')}
      </p>
      <Link to="/register?type=owner" className="text-sm font-semibold text-[#F97316] hover:underline" data-testid="noc-packages-cta">{t('Get started')}</Link>
    </div>
  </section>
);

export default NocPackagesPromo;
